import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import VisibilityIcon from "@mui/icons-material/Visibility";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";
import { Collapse, IconButton } from "@mui/material";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import { useMemo, useState } from "react";
import { getCategoryColor, getCategoryLabel } from "../aiMappingColors";
import { countActiveDetections, groupDetectionsByCategory } from "../aiMappingDetections";
import { AiMappingController } from "../useAiMappingController";

export type ManualMeasurement = {
  id: string;
  label: string;
  color: string;
  areaSqft: number;
  areaSqyd: number;
  linearFeet: number;
  linearYards: number;
};

const STALL_LINEAR_FEET: Record<string, number> = {
  single_stalls: 20,
  double_stalls: 25,
};

function SummaryDrawer({
  title,
  subtitle,
  open,
  onToggle,
  actions,
  children,
}: {
  title: string;
  subtitle: string;
  open: boolean;
  onToggle: () => void;
  actions?: React.ReactNode;
  children: React.ReactNode;
}) {
  return (
    <MDBox p={1} sx={{ border: "1px solid #e7ebf0", borderRadius: "14px", bgcolor: "#fff" }}>
      <MDBox display="flex" alignItems="center" gap={1}>
        <MDBox sx={{ flex: 1, cursor: "pointer" }} onClick={onToggle}>
          <MDTypography variant="button" fontWeight="medium">
            {title}
          </MDTypography>
          <MDTypography
            variant="caption"
            color="text"
            display="block"
            sx={{ whiteSpace: "pre-line" }}
          >
            {subtitle}
          </MDTypography>
        </MDBox>
        {actions}
        <IconButton size="small" onClick={onToggle}>
          <ExpandMoreIcon fontSize="small" sx={{ transform: open ? "rotate(180deg)" : "none" }} />
        </IconButton>
      </MDBox>
      <Collapse in={open}>
        <MDBox pt={1}>{children}</MDBox>
      </Collapse>
    </MDBox>
  );
}

function VisibilityToggle({ visible, onToggle }: { visible: boolean; onToggle: () => void }) {
  return (
    <IconButton size="small" onClick={onToggle}>
      {visible ? <VisibilityIcon fontSize="small" /> : <VisibilityOffIcon fontSize="small" />}
    </IconButton>
  );
}

export default function AiMappingGlobalMeasurements({
  controller,
  combinedMeasuredAreaSqft,
  manualPerimeterFeet,
  manualMeasurements,
}: {
  controller: AiMappingController;
  combinedMeasuredAreaSqft: number;
  manualPerimeterFeet: number;
  manualMeasurements: ManualMeasurement[];
}) {
  const [countsOpen, setCountsOpen] = useState(true);
  const [areasOpen, setAreasOpen] = useState(false);

  const detections = useMemo(
    () => controller.allResults.flatMap((result) => result.detections),
    [controller.allResults]
  );
  const countGroups = useMemo(() => groupDetectionsByCategory(detections), [detections]);
  const segmentationGroups = useMemo(() => {
    const groups = new Map<string, { color: string; count: number; sqft: number }>();
    controller.allResults.forEach((result) =>
      result.segmentations
        .filter((segmentation) => !segmentation.isDeleted)
        .forEach((segmentation) => {
          const color = getCategoryColor(segmentation.category, segmentation.color);
          const current = groups.get(color) ?? { color, count: 0, sqft: 0 };
          current.count += 1;
          current.sqft += segmentation.areaSqft ?? 0;
          groups.set(color, current);
        })
    );
    return Array.from(groups.values()).sort((a, b) => b.sqft - a.sqft);
  }, [controller.allResults]);

  return (
    <MDBox
      p={1}
      display="flex"
      flexDirection="column"
      gap={0.75}
      data-testid="ai-mapping-global-measurements"
      sx={{ border: "1px solid #d1d5db", borderRadius: "16px", bgcolor: "#fff" }}
    >
      <MDTypography variant="button" fontWeight="medium" color="info">
        All Area Totals
      </MDTypography>

      <SummaryDrawer
        title="Counts"
        subtitle={`${countActiveDetections(detections).toLocaleString()} total counted items`}
        open={countsOpen}
        onToggle={() => setCountsOpen((open) => !open)}
        actions={
          <VisibilityToggle
            visible={controller.detectionsVisible}
            onToggle={() => controller.setDetectionsVisible(!controller.detectionsVisible)}
          />
        }
      >
        {countGroups.length === 0 ? (
          <MDTypography variant="body2" color="text" textAlign="center">
            No AI counts yet. Select a Run Area to run detection.
          </MDTypography>
        ) : (
          <MDBox display="flex" flexDirection="column" gap={0.5}>
            {countGroups.map((group) => {
              const visible =
                controller.detectionsVisible && controller.isDetectionGroupVisible(group.category);
              const feetPerStall = STALL_LINEAR_FEET[group.category];
              return (
                <MDBox
                  key={group.category}
                  display="flex"
                  alignItems="center"
                  gap={0.75}
                  p={0.75}
                  sx={{ borderRadius: "10px", bgcolor: "#fafbfc" }}
                >
                  <MDBox
                    sx={{
                      width: 12,
                      height: 12,
                      borderRadius: "50%",
                      bgcolor: group.color,
                    }}
                  />
                  <MDBox sx={{ flex: 1 }}>
                    <MDTypography variant="button">{getCategoryLabel(group.category)}</MDTypography>
                    {feetPerStall != null && (
                      <MDTypography variant="caption" color="text" display="block">
                        {(group.count * feetPerStall).toLocaleString()} linear ft
                      </MDTypography>
                    )}
                  </MDBox>
                  <VisibilityToggle
                    visible={visible}
                    onToggle={() => controller.toggleDetectionGroupVisible(group.category)}
                  />
                  <MDTypography variant="button" fontWeight="bold">
                    {group.count}
                  </MDTypography>
                </MDBox>
              );
            })}
          </MDBox>
        )}
      </SummaryDrawer>

      <SummaryDrawer
        title="Measured Areas"
        subtitle={`Total Area: ${Math.round(
          combinedMeasuredAreaSqft
        ).toLocaleString()}ft² / ${Math.round(combinedMeasuredAreaSqft / 9).toLocaleString()}yd²
Total Perimeter: ${Math.round(manualPerimeterFeet).toLocaleString()}ft / ${Math.round(
          manualPerimeterFeet / 3
        ).toLocaleString()}yd`}
        open={areasOpen}
        onToggle={() => setAreasOpen((open) => !open)}
        actions={
          <VisibilityToggle
            visible={controller.segmentationsVisible}
            onToggle={() => controller.setSegmentationsVisible(!controller.segmentationsVisible)}
          />
        }
      >
        {segmentationGroups.length === 0 && manualMeasurements.length === 0 ? (
          <MDTypography variant="body2" color="text" textAlign="center">
            No measured areas yet. Draw a map measurement or run segmentation.
          </MDTypography>
        ) : (
          <MDBox display="flex" flexDirection="column" gap={0.5}>
            {segmentationGroups.map((group) => (
              <MDBox
                key={group.color}
                display="flex"
                alignItems="center"
                gap={0.75}
                p={0.75}
                sx={{ borderRadius: "10px", bgcolor: "#fafbfc" }}
              >
                <MDBox
                  sx={{
                    width: 12,
                    height: 12,
                    borderRadius: "50%",
                    bgcolor: group.color,
                  }}
                />
                <MDTypography variant="button" sx={{ flex: 1 }}>
                  {group.count} {group.count === 1 ? "AI area" : "AI areas"}
                </MDTypography>
                <MDTypography variant="caption" color="text">
                  {Math.round(group.sqft).toLocaleString()} sqft
                </MDTypography>
              </MDBox>
            ))}
            {manualMeasurements.map((measurement) => (
              <MDBox
                key={measurement.id}
                display="flex"
                alignItems="center"
                gap={0.75}
                p={0.75}
                sx={{ borderRadius: "10px", bgcolor: "#fafbfc" }}
              >
                <MDBox
                  sx={{
                    width: 12,
                    height: 12,
                    borderRadius: "50%",
                    bgcolor: measurement.color,
                  }}
                />
                <MDTypography variant="button" noWrap sx={{ flex: 1 }}>
                  {measurement.label}
                </MDTypography>
                <MDTypography variant="caption" color="text">
                  {measurement.areaSqft > 0
                    ? `${Math.round(measurement.areaSqft).toLocaleString()} sqft / ${Math.round(
                        measurement.areaSqyd
                      ).toLocaleString()} sq yd`
                    : `${Math.round(measurement.linearFeet).toLocaleString()} ft / ${Math.round(
                        measurement.linearYards
                      ).toLocaleString()} yd`}
                </MDTypography>
              </MDBox>
            ))}
          </MDBox>
        )}
      </SummaryDrawer>
    </MDBox>
  );
}
